/**
 * "Revisa los días antes de imputar": the confirmation behind "Completar lo
 * recomendado" (and behind a single day's "Completar").
 *
 * The Manager does not know a person's holidays or local festivities, so every
 * day is listed with its own checkbox and the person confirms each one; "Estuve
 * ausente" turns a day into one absence entry instead.
 */
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import type { ImputacionMonth, IsoDate } from '../../../types/imputacion'
import {
    type Draft,
    absenceDraftForDay,
    absenceLine,
    absenceTargetForDay,
    buildFillDraft,
    canMarkAbsent,
    ddmm,
    fillBreakdown,
    findDay,
    fmtH,
    fmtNum,
    round2,
    weekdayShort,
} from './imputacionLogic'

export interface FillConfirmModalProps {
    month: ImputacionMonth
    days: IsoDate[]
    /** Days ticked when the modal opens. */
    initiallyChecked: IsoDate[]
    /** Gives back the draft for the ticked days; `send` = "Imputar en Redmine". */
    onConfirm: (fill: Draft, send: boolean) => void
    onClose: () => void
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Modal = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  width: 640px;
  max-width: 94vw;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
`

const Head = styled.div`
  padding: 1rem 1.25rem 0.5rem;

  h2 {
    margin: 0 0 6px;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors.primary};
  }
`

const Body = styled.div`
  padding: 0 1.25rem;
  overflow-y: auto;
`

const Warning = styled.div`
  background: #FFF8E1;
  border-left: 4px solid #FF9800;
  padding: 8px 10px;
  margin: 6px 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const DayRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  padding: 6px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSizes.sm};

  label {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    cursor: pointer;
  }
`

const DayHint = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: #E65100;
`

const Small = styled.button`
  margin-left: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: white;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  cursor: pointer;
`

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0.75rem 1.25rem 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const Btn = styled.button<{ $primary?: boolean }>`
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ $primary, theme }) => ($primary ? theme.colors.primary : 'white')};
  color: ${({ $primary, theme }) => ($primary ? 'white' : theme.colors.primary)};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const HINTS: Record<string, string> = {
    junto_ausencia: '⚠ junto a vacaciones',
    agosto: '⚠ agosto',
    posible_festivo: '⚠ puede ser festivo local',
}

export function FillConfirmModal({ month, days, initiallyChecked, onConfirm, onClose }: FillConfirmModalProps) {
    const [checked, setChecked] = useState<Set<IsoDate>>(() => new Set(initiallyChecked))
    const [absent, setAbsent] = useState<Set<IsoDate>>(() => new Set())
    const absenceOk = canMarkAbsent(month)
    const absence = absenceLine(month)

    const toggle = (set: Set<IsoDate>, iso: IsoDate) => {
        const next = new Set(set)
        if (next.has(iso)) next.delete(iso)
        else next.add(iso)
        return next
    }

    /** The draft the ticked days produce: the recommendation, or one absence cell. */
    const fill = useMemo(() => {
        const ticked = days.filter((d) => checked.has(d))
        let draft = buildFillDraft(
            month,
            ticked.filter((d) => !absent.has(d)),
        )
        for (const d of ticked.filter((x) => absent.has(x))) draft = absenceDraftForDay(month, d, draft)
        return draft
    }, [month, days, checked, absent])

    const dayHours = (iso: IsoDate) => {
        if (absent.has(iso)) return round2(absenceTargetForDay(month, iso) - (absence?.logged[iso] ?? 0))
        return round2(fillBreakdown(month, iso).reduce((s, x) => s + x.hours, 0))
    }
    const tickedDays = days.filter((d) => checked.has(d))
    const totalHours = round2(tickedDays.reduce((s, d) => s + dayHours(d), 0))

    return (
        <Overlay role="presentation" onClick={onClose}>
            <Modal role="dialog" aria-modal="true" aria-labelledby="imp-fill-title" onClick={(e) => e.stopPropagation()}>
                <Head>
                    <h2 id="imp-fill-title">Revisa los días antes de imputar</h2>
                    <div>No imputes festivos ni vacaciones. Desmarca los días en que no trabajaste.</div>
                    {!month.calendar_complete && (
                        <Warning>
                            Los festivos locales aún no están configurados en el Manager: desmarca los días que sean festivo.
                        </Warning>
                    )}
                </Head>
                <Body>
                    {days.map((iso) => {
                        const day = findDay(month, iso)
                        const isAbsent = absent.has(iso)
                        const parts = isAbsent
                            ? `${absence?.label ?? 'Vacaciones'} ${fmtNum(dayHours(iso))}`
                            : fillBreakdown(month, iso)
                                  .map((x) => `${x.line.label} ${fmtNum(x.hours)}`)
                                  .join(' + ')
                        return (
                            <DayRow key={iso} data-day={iso}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={checked.has(iso)}
                                        onChange={() => setChecked((s) => toggle(s, iso))}
                                        aria-label={`${weekdayShort(iso)} ${ddmm(iso)}`}
                                    />
                                    <span>
                                        {weekdayShort(iso)} {ddmm(iso)} · {fmtH(dayHours(iso))} · {parts}
                                    </span>
                                </label>
                                {day?.state === 'today' && <DayHint>hoy</DayHint>}
                                {day?.hint && HINTS[day.hint] && <DayHint>{HINTS[day.hint]}</DayHint>}
                                {absenceOk && (
                                    <Small type="button" onClick={() => setAbsent((s) => toggle(s, iso))}>
                                        {isAbsent ? 'Deshacer ausencia' : 'Estuve ausente'}
                                    </Small>
                                )}
                            </DayRow>
                        )
                    })}
                </Body>
                <Foot>
                    <strong>
                        {tickedDays.length} {tickedDays.length === 1 ? 'día' : 'días'} · {fmtH(totalHours)}
                    </strong>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn type="button" onClick={onClose}>
                            Cancelar
                        </Btn>
                        <Btn type="button" disabled={!tickedDays.length} onClick={() => onConfirm(fill, false)}>
                            Revisar en la tabla
                        </Btn>
                        <Btn type="button" $primary disabled={!tickedDays.length} onClick={() => onConfirm(fill, true)}>
                            Imputar en Redmine
                        </Btn>
                    </div>
                </Foot>
            </Modal>
        </Overlay>
    )
}
